<template>
    <div class="scroll-horizontal"
         :class="{
             [`scroll-horizontal--offset-${offset}`] : offset !== '',
             [`scroll-horizontal--padding-${padding}`] : padding !== '',
             [`scroll-horizontal--${paddingColor}`] : paddingColor !== '',
             [`scroll-horizontal--${activeOn}`] : activeOn !== '',
         }"
    >
        <div class="scroll-horizontal__inner">
            <slot />
        </div>
    </div>
</template>

<script>
export default {
    name: 'ScrollHorizontal',
    props: {
        offset: {
            type: String,
            default: '',
            validator: value => ['', 'md', 'lg'].includes(value),
        },
        padding: {
            type: String,
            default: '',
            validator: value => ['', 'md', 'lg'].includes(value),
        },
        paddingColor: {
            type: String,
            default: 'white',
            validator: value => ['', 'white', 'light', 'dark'].includes(value),
        },
        activeOn: {
            type: String,
            default: 'tablet-portrait',
            validator: value => ['', 'tablet-landscape', 'tablet-portrait'].includes(value),
        },
    },
};
</script>

<style scoped lang="scss">
$scroll-class: '.scroll-horizontal';

@mixin set-scroll-color($color) {
    &::before {
        background-image: linear-gradient(to right, $color 25%, rgba($color, 0));
    }

    &::after {
        background-image: linear-gradient(to left, $color 25%, rgba($color, 0));
    }
}

@mixin set-offset($offset) {
    width: calc(100% + #{$offset*2});
    margin-right: -$offset;
    margin-left: -$offset;

    #{$scroll-class}__inner {
        padding-right: $offset;
        padding-left: $offset;
    }

    &::before,
    &::after {
        width: $offset;
    }
}

@mixin set-padding($padding) {
    #{$scroll-class}__inner {
        padding-right: $padding;
        padding-left: $padding;
    }

    &::before,
    &::after {
        width: $padding;
    }
}

@mixin make-scroll() {
    position: relative;

    #{$scroll-class}__inner {
        overflow-x: auto;
        white-space: nowrap;

        // Make it smooth scrolling on iOS devices
        -webkit-overflow-scrolling: touch;

        // Hide the scrollbars in Edge until the scrollable area is hovered
        -ms-overflow-style: -ms-autohiding-scrollbar;

        // Hide the scroll bar in WebKit browsers
        &::-webkit-scrollbar {
            display: none;
        }
    }

    // gradient overlay scrolling sides
    &::before,
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: 0; // width is set on modifier
        background: transparent no-repeat scroll center; // color is set on modifier
    }

    &::before {
        left: 0;
        background-position: left center;
    }

    &::after {
        right: 0;
        background-position: right center;
    }

    &#{$scroll-class}--white {
        @include set-scroll-color($color-white);
    }

    &#{$scroll-class}--light {
        @include set-scroll-color($color-background-light-3);
    }

    &#{$scroll-class}--dark {
        @include set-scroll-color($color-background-dark);
    }

    &#{$scroll-class}--offset-md {
        @include set-offset(20px);
    }

    &#{$scroll-class}--offset-lg {
        @include set-offset(30px);
    }

    &#{$scroll-class}--padding-md {
        @include set-padding(20px);
    }

    &#{$scroll-class}--padding-lg {
        @include set-padding(30px);
    }
}

#{$scroll-class} {
    &#{$scroll-class}--tablet-landscape {
        @include respond-to($media-tablet, max) {
            @include make-scroll();
        }
    }

    &#{$scroll-class}--tablet-portrait {
        @include respond-to($media-mobile, max) {
            @include make-scroll();
        }
    }
}
</style>
